import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2 } from 'lucide-react';

import { env } from '@/env';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import { useAccettaInvito } from '@/hooks/useAccettaInvito';
import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { useStartMatch } from '@/hooks/useStartMatch';
import { useHaptics } from '@/hooks/useHaptics';
import { useLandingNotice } from '@/contexts/LandingNoticeContext';
import { inviteNotice, type InviteOutcome } from '@/lib/landingNotice';
import { propostoNellAnteprima } from '@/lib/qgammon/allenamentoInvito';

/**
 * ALLENAMENTO FRA AMICI (cantiere 34): se l'anteprima propone l'allenamento, al posto dell'unico «Accetta» la scelta ESPLICITA «con» / «senza»
 * (chunk lazy dell'allenamento). Solo nel ramo del LETTERALE del flag del coach: questo file e' condiviso e in produzione compila agli stessi byte
 * (V11, P4 = 0). La risposta arriva al corpo dell'accept dalla casella (`corpoAccettazione`), mai da un parametro nuovo.
 */
const SceltaAccettazioneLazy =
  import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'
    ? lazy(() => import('@/components/backgammon/allenamento/AllenamentoUi').then((m) => ({ default: m.SceltaAccettazione })))
    : null;

/**
 * `/invito/:code` — dove atterra chi apre `t.me/<bot>?startapp=inv_bg_<CODICE>`.
 *
 * ═══ PERCHE' LA ROTTA E' `/invito/<codice>` E NON `/backgammon/match/<id>` ═══
 * 🔑 **Non esiste un match a cui puntare.** L'invito QGammon e' un bearer
 * monouso da 15 minuti, e il match lo CREA l'accettazione (`POST
 * /invites/:code/accept` → `registry.create`). Un link a `/backgammon/match/<id>`
 * o non avrebbe un id da mettere, o ne avrebbe uno a cui l'ospite non ha ancora
 * diritto. E' una decisione di MODELLO, non di naming.
 *
 * ═══ PERCHE' STA FUORI DA `backgammonRoutes` ═══
 * Quelle rotte esistono solo se `featureBackgammonEnabled`, e sono dentro
 * `<RoleGate role="beta">`. Un ospite senza il ruolo, o su una build col flag
 * spento, cadrebbe sul catch-all `*` e verrebbe rimandato alla Hub — cioe'
 * ESATTAMENTE il fallimento silenzioso che il deep link doveva evitare.
 * Qui invece la schermata esiste sempre e dice quale dei casi e'.
 * 📌 [M 06/09] In PRODUZIONE `VITE_FEATURE_BACKGAMMON_ENABLED=false`; su
 * staging e' `true`. Un link di prod oggi non puo' arrivare a una partita, e
 * l'ospite ha diritto di leggerlo invece di indovinarlo.
 *
 * ═══ I RIFIUTI NON SONO CASI LIMITE ═══
 * Sono il comportamento normale di un bearer: chiunque abbia il codice puo'
 * accettarlo, una volta sola, entro 15 minuti. Quindi 404 (scaduto, gia' preso
 * da un altro, o annullato da chi l'ha mandato), 400 `cannot_accept_own`, 409
 * `too_many_active_matches` (con `who`, che dice CHI dei due e' pieno) sono
 * esiti previsti, e ognuno ha una frase che dice cosa e' successo E cosa si
 * puo' fare. ⛔ Nessuno di questi e' «errore».
 *
 * ═══ 🔴 ADR-0115 — QUESTA ROTTA NON RENDE PIU' I RIFIUTI ═══
 * Li ALZA come avviso e se ne va. Nove esiti su nove diventano una modale sopra
 * una superficie viva (`LandingNoticeModal`), e questa rotta resta responsabile
 * di UNA cosa sola: l'atterraggio riuscito — anteprima e accettazione.
 * 🔑 Il ramo di successo NON e' una modale, ed e' voluto: accettare un invito
 * non e' un errore di atterraggio, e' l'atterraggio. L'ADR governa il
 * fallimento, non il resto della schermata.
 * ⚠️ `navigate('/', { replace: true })` e non un `push`: il tasto indietro non
 * deve poter tornare su una rotta che si e' appena dichiarata inutilizzabile —
 * tornarci rifarebbe partire l'avviso da capo.
 */

interface InvitePreview {
  creator: { username: string; displayName: string; elo: number };
  matchLength: number;
  expiresAt: string;
  /** Allenamento fra amici (cantiere 34): `"proposto"` se chi invita lo propone. Dato sconosciuto ai tipi 0.23 (V10). */
  allenamento?: unknown;
}

/**
 * ⚠️ `expired` e `gone` sono DUE cause che il server manda con lo STESSO 404.
 * `peekInvite` nullo e `claimInvite` nullo danno entrambi `not_found`: dalla
 * risposta, da sola, non si distinguono. Le distingue la SEQUENZA — v.
 * `refusalFor404`. E' la stessa contromisura che la sezione Limulo usa contro il
 * 424 muto: quando il server non porta la causa, la si ricava da cio' che si e'
 * visto un attimo prima.
 */
type Refusal = 'expired' | 'gone' | 'own' | 'capYou' | 'capCreator' | 'unreachable';

/**
 * Perche' questo invito non c'e' piu'.
 *
 * 🔑 Il caso che rende la distinzione necessaria e' NUOVO col deep link: un
 * codice letto a voce e' quasi sempre «scaduto», ma un LINK inoltrato in un
 * gruppo rende normale «l'ha gia' preso un altro» — e dire «scaduto» a chi ha
 * appena perso una corsa e' dirgli la cosa sbagliata, per giunta suggerendogli
 * di aspettare invece di chiedere un invito nuovo.
 *
 * ⛔ E non si afferma piu' di quel che si sa: preview vista + tempo residuo +
 * accept 404 esclude la scadenza, NON dice chi. Poteva anche essere il creatore
 * che ha annullato (`DELETE /invites/:code` → stesso 404). La copy nomina
 * entrambe le cause invece di sceglierne una che suona meglio.
 */
function refusalFor404(preview: InvitePreview | null, now: number): 'expired' | 'gone' {
  // Mai vista viva: non e' mai esistita, o prima che arrivassimo e' scaduta, l'ha presa un altro o il
  // creatore l'ha annullata. (Qui in pratica non si arriva: si chiama solo dopo un'anteprima riuscita. Il
  // caso «mai vista viva» vero e' il 404 dell'anteprima, piu' sotto nell'effetto.)
  if (!preview) return 'expired';
  const expiresAt = Date.parse(preview.expiresAt);
  // Il tempo e' finito mentre l'ospite leggeva: la scadenza e' la causa vera.
  if (!Number.isFinite(expiresAt) || expiresAt <= now) return 'expired';
  // Era viva un attimo fa e adesso non c'e' piu': non e' il tempo.
  return 'gone';
}

/** Stesso alfabeto/lunghezza di `AcceptInviteSheet` e di `lib/startParam`. */
const CODE_RE = /^[A-Z2-9]{10}$/;

export function InviteLanding() {
  const { t } = useTranslation('backgammon');
  const { code: rawCode } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const startMatch = useStartMatch();
  const haptics = useHaptics();
  const isBeta = useHasLogtoRole('beta');
  const { raise } = useLandingNotice();
  const { accetta: accettaInvito, accettando } = useAccettaInvito();

  const code = (rawCode ?? '').toUpperCase();
  const codeLooksValid = CODE_RE.test(code);

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  // La build non ha la sezione, oppure l'utente non ha il ruolo: la chiamata al
  // gameserver non si fa proprio. Chiedere per poi non poter navigare sarebbe
  // bruciare l'invito — che e' MONOUSO — per una partita irraggiungibile.
  const featureOff = !env.featureBackgammonEnabled;
  const roleMissing = !isBeta;
  const blocked = featureOff || roleMissing;

  useEffect(() => {
    if (blocked || !codeLooksValid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${env.qgammonWsUrl}/invites/${encodeURIComponent(code)}`, {
          headers: await qgammonAuthHeaders(),
        });
        if (cancelled) return;
        if (res.status === 404) {
          // Mai vista viva: mai esistita, scaduta, presa da un altro, o annullata da chi l'ha mandata — il
          // 404 non dice quale. ⚠️ Col ponte delle sfide fra amici (L-2288) l'annullo e' NORMALE: chiudere il
          // pannello annulla l'invito gia' recapitato in DM (collaudo FASE 3, 26/09 13:30Z). E' il ramo
          // tipico di quel caso, e la copy di `expired` lo nomina invece di dire «scaduto» a chi ha in mano
          // un invito di due minuti fa. (Se l'ospite aveva gia' l'anteprima quando il creatore ha chiuso, il
          // caso passa dal 404 di `accept` e finisce in `gone`, la cui copy nomina gia' l'annullo.)
          setRefusal('expired');
          return;
        }
        if (!res.ok) throw new Error(`preview failed: ${res.status}`);
        setPreview((await res.json()) as InvitePreview);
      } catch {
        if (!cancelled) setRefusal('unreachable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blocked, code, codeLooksValid]);

  // 🔴 La chiamata vive in `useAccettaInvito` (I-252, T1-7): la carta-sfida nel DM la riusa, invece
  // di riscrivere il parsing degli stessi rifiuti. Qui restano SOLO le due finezze che dipendono
  // dalla `preview` di QUESTA pagina (`gone` vs `expired`) — la carta nel DM non le ha.
  const accept = useCallback(async () => {
    setRefusal(null);
    const esito = await accettaInvito(code);
    if (esito.ok) {
      // 🔴 `status` NON e' un extra: ADR-0114 dice che accettare un invito crea
      // un APPUNTAMENTO, e il server lo dichiara qui
      // (`qgammon-gameserver/src/invites/routes.ts:266` @ origin/main, col suo
      // commento: «The acceptor learns it is an appointment, not a board to
      // start playing on»). Leggere solo `{matchId, color}` e' cio' che faceva
      // scrivere «In gioco» a una partita non iniziata.
      // ⚠️ Il campo viaggia nel descrittore per il PRIMO DISEGNO; l'autorita'
      // resta `match:state` sul socket — v. `lib/qgammon/matchStatus.ts`.
      haptics.notification('success');
      startMatch({ game: 'qgammon', matchId: esito.matchId, perspective: esito.color, status: esito.status });
      return;
    }
    if (esito.motivo === 'proprio') {
      setRefusal('own');
    } else if (esito.motivo === 'non-trovato') {
      // La preview era andata a buon fine un istante fa: se l'invito ora non
      // c'e' piu' e aveva ancora tempo, la scadenza NON e' la causa.
      setRefusal(refusalFor404(preview, Date.now()));
    } else if (esito.motivo === 'troppe-partite') {
      setRefusal(esito.chi === 'creatore' ? 'capCreator' : 'capYou');
    } else {
      setRefusal('unreachable');
    }
  }, [code, haptics, startMatch, preview, accettaInvito]);

  // ── L'ESITO, in ordine di precedenza ────────────────────────────────────
  // L'ordine conta e non e' cambiato: un codice malformato non e' «scaduto», e
  // una build senza la sezione non e' «invito non valido». Ogni ramo nomina la
  // SUA causa. Cio' che e' cambiato e' dove finisce la frase, non quale sia.
  const outcome: InviteOutcome | null = !codeLooksValid
    ? 'malformed'
    : featureOff
      ? 'unavailable'
      : roleMissing
        ? 'beta'
        : refusal;

  // ⚠️ In un effetto e non nel corpo del render: alzare l'avviso scrive nello
  // stato di un ALTRO componente, e navigare durante un render e' lo stesso
  // difetto con un altro nome.
  useEffect(() => {
    if (!outcome) return;
    raise({
      notice: inviteNotice(outcome, { canReachBackgammon: !blocked }),
      // Riprovare significa RI-ATTERRARE su questo codice. Da qui a un istante
      // la rotta sara' la Hub: un `reload()` ricaricherebbe quella, cioe'
      // butterebbe via il codice invece di rileggerlo.
      retryTo: `/invito/${encodeURIComponent(code)}`,
    });
    navigate('/', { replace: true });
  }, [outcome, blocked, code, raise, navigate]);

  // Si sta ancora decidendo, oppure si sta gia' uscendo verso la Hub: in
  // entrambi i casi non c'e' niente da mostrare QUI. La frase e' sulla modale.
  if (loading || outcome) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="invite-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 p-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <KeyRound className="h-6 w-6 text-brand" />
        <h1 className="text-lg font-bold text-fg">{t('invite.landing.title')}</h1>
        {preview && (
          <p className="text-sm text-fg-muted">
            {t('invite.preview', {
              name: preview.creator.displayName || preview.creator.username,
              elo: preview.creator.elo,
              length: preview.matchLength,
            })}
          </p>
        )}
      </div>
      {import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' && SceltaAccettazioneLazy && preview && propostoNellAnteprima(preview) ? (
        <Suspense fallback={null}>
          <SceltaAccettazioneLazy
            codice={code}
            nome={preview.creator.displayName || preview.creator.username}
            inCorso={accettando}
            onAccetta={() => void accept()}
            forma="pagina"
          />
        </Suspense>
      ) : (
      <button
        type="button"
        disabled={accettando}
        onClick={() => void accept()}
        className="rounded-md bg-brand px-8 py-3 text-base font-medium text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
      >
        {accettando ? t('invite.creating') : t('invite.accept')}
      </button>
      )}
    </div>
  );
}
